import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CurrencyRupeeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  TruckIcon,
  HeartIcon,
  PaperAirplaneIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  UsersIcon,
  GlobeAltIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, Badge, getStatusBadgeVariant, SearchableSelect, Select } from '@/components/common';
import { PolicyNumber } from '@/components/common/PolicyNumber';
import { PageHeader } from '@/components/layout';
import { dashboardService, renewalService, api } from '@/services';
import { getPlatformStats } from '@/services/users';
import { useAuthStore } from '@/store';
import { formatCurrency, formatDate, titleCase } from '@/utils';
import { Link, useNavigate } from 'react-router-dom';
import { resolveCustomerMicrosite, micrositeTypeUrl } from '@/pages/customers/micrositeTarget';
import type { PolicyBrokerRef, PublicBrokerRow } from '@/pages/customers/micrositeTarget';
import type { InsuranceTypeLookup } from '@/hooks/useProducts';
import type { Policy, UserRole } from '@/types';
import type { RenewalQueueFilters, RenewalQueueRow } from '@/types/renewalQueue';
// B-01/B-02: the dashboard widget now speaks the SAME renewal vocabulary as
// the Renewals list page, imported from it rather than re-derived here -- one
// more place inventing its own idea of "upcoming" is exactly how the hidden
// window bugs (#220/#246) happened. Imported from the modules, not the
// `pages/renewals` barrel, so this needs no edit to a shared index.
import {
  EXPIRY_WINDOW_LAPSED,
  EXPIRY_WINDOW_UPCOMING,
  lapsedWindowOptionsFor,
  upcomingWindowOptions,
  windowToQueueParams,
  type RenewalTab,
} from '@/pages/renewals/renewalExpiryWindow';
import { monthOptionsFor, yearOptionsFor } from '@/pages/renewals/renewalMonthYear';
import { RenewalTabList } from '@/pages/renewals/RenewalTabList';
import { DEFAULT_UPCOMING_RENEWALS_DAYS } from './upcomingRenewalsQuery';
import { COMMISSION_CARD_LABEL, COMMISSION_CARD_TOOLTIP } from './commissionCardCopy';
import {
  ACTIVE_POLICIES_TARGET,
  COUNTRIES_TARGET,
  COUNTRY_ADMINS_TARGET,
  TOTAL_POLICIES_TARGET,
  pendingQuotesTarget,
} from './cardTargets';

// W0-D, carried into B9: `RenewalQueueViewSet.get_permissions`
// (backend/apps/renewals/queue_views.py) admits the same set the old
// `RenewalViewSet` did -- IsBrokerEmployee (super_admin/country_admin/
// broker_admin/broker_employee) or a customer (scoped to their own book,
// §2.5). posp/super_posp/insurer_admin/insurer_employee/vendor_admin/
// audit_admin all get a 403 on GET /renewals/queue/. Board: 152 403s since 7
// Sep against the old endpoint, rendered by the widget below as "No upcoming
// renewals" -- indistinguishable from an empty book. Mirrored here (not
// derived from a shared constant: the frontend has no import path into the
// backend's permission classes) so a role the API refuses never fires the
// request at all; the alternative -- a POSP-scoped renewals source
// (`POSPViewSet.dashboard`, backend/apps/brokers/views.py ~2657, url_path
// `dashboard`, NOT a `dashboard-stats` action on BrokerViewSet at ~2101 as
// this comment used to say) returns only a `pending_renewals` COUNT -- and
// that count is itself upcoming-30 plus lapsed added together, so it cannot
// feed the row shape this table renders. B-01/B-02 therefore stop at broker/customer: opening a POSP
// renewals surface needs a product decision nobody has made (assigned-to-
// agent, which `queue.scoped_queue_policies` enforces, vs sold-by-agent,
// which this `dashboard` count uses -- the two shipped call sites genuinely
// disagree).
const RENEWALS_WIDGET_ROLES: ReadonlySet<UserRole> = new Set([
  'super_admin',
  'country_admin',
  'broker_admin',
  'broker_employee',
  'customer',
]);

// Batch 2026-09-18, item 22: Country Admin's dashboard is being decluttered --
// Upcoming Renewals here duplicated /renewals, which is Country Admin's actual
// work surface, not a summary widget. The API still admits the role (it stays
// in RENEWALS_WIDGET_ROLES above, which mirrors what the backend permits, not
// what this page chooses to show), so this is a display decision layered on
// top and kept as its own set rather than folded into a role check inline --
// a future change to what the API allows must not silently re-show this.
//
// B-01/B-02: `super_admin` joins it, for a different reason. A super_admin's
// policy queryset is UNRESTRICTED (`queue.scoped_queue_policies` falls through
// to `scoped_policy_queryset`), so the queue returns a CROSS-BROKER platform
// book for them -- a live reading of lapsed=15 / upcoming=821, against one
// brokerage's 6/756. (The widget's tab badges showed those totals until
// Cust-4/U-03 removed them; the rows are just as cross-broker.) Unlabelled,
// that presents one operator with every other brokerage's book as if it were
// their own. The rest of this page already keeps super_admin out of the whole
// broker-shaped section (the `!isSuperAdmin` wrapper below, FIX 1A: their
// front page is Country Admins + Countries), so today the widget never
// renders for them anyway -- which is exactly why this belongs in the set as
// well: the day someone lifts the widget out of that wrapper, the unlabelled
// cross-tenant book must not ship with it. A platform-wide renewals view is
// a real thing to want, but it needs to SAY it is platform-wide, and that is
// a surface nobody has asked for yet.
const HIDE_RENEWALS_WIDGET_ROLES: ReadonlySet<UserRole> = new Set([
  'country_admin',
  'super_admin',
]);

interface InsuranceTile {
  name: string;
  type: string;
  icon: typeof TruckIcon;
  color: string;
}

// Icon + colour per insurance-type CODE. Only presentation lives here: the
// NAMES come from the configured types (F6 step 4), so a renamed or added type
// shows up as itself, and a code we have no artwork for still renders (generic
// document icon) instead of being dropped.
const TILE_STYLES: Record<string, { icon: typeof TruckIcon; color: string }> = {
  motor: { icon: TruckIcon, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  health: { icon: HeartIcon, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  life: { icon: UserIcon, color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' },
  travel: { icon: PaperAirplaneIcon, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' },
  term: { icon: ShieldCheckIcon, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
};
const DEFAULT_TILE_STYLE = {
  icon: DocumentTextIcon,
  color: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400',
};

// The floor: what a customer sees when the configured-types lookup is empty or
// unreachable. `type` is the slug the in-app catalog route (/buy/:type) takes.
const BUILT_IN_INSURANCE_TYPES = [
  { name: 'Motor Insurance', type: 'motor' },
  { name: 'Health Insurance', type: 'health' },
  { name: 'Life Insurance', type: 'life' },
  { name: 'Travel Insurance', type: 'travel' },
  { name: 'Term Plan', type: 'term' },
  { name: 'Other', type: 'other' },
];

function toTile({ name, type }: { name: string; type: string }): InsuranceTile {
  return { name, type, ...(TILE_STYLES[type] ?? DEFAULT_TILE_STYLE) };
}

/**
 * The AllowAny public broker directory, paginated (`max_page_size = 100`) and
 * addressable only by slug, so membership has to be tested against the whole
 * list. Capped at 10 pages: a platform with a runaway broker count degrades to
 * "no microsite" (the in-app catalog) rather than to a stalled dashboard.
 *
 * Deliberately shares the `public-broker-directory` query key with
 * BuyPolicyPage, so whichever screen the customer opens second pays nothing.
 * (The fetcher itself is duplicated only because it is a page-local function
 * there; hoisting it belongs with micrositeTarget.ts.)
 */
async function fetchPublicBrokers(): Promise<PublicBrokerRow[]> {
  const rows: PublicBrokerRow[] = [];
  let url: string | null = '/policy/api/public/brokers/?page_size=100';
  for (let page = 0; url && page < 10; page += 1) {
    // Annotated (not inferred): `url` is reassigned from `body.next`, and
    // inferring `body` from the awaited call makes that a circular reference.
    const body: { results?: PublicBrokerRow[]; next?: string | null } | PublicBrokerRow[] = (
      await axios.get(url)
    ).data;
    if (Array.isArray(body)) {
      rows.push(...body);
      url = null;
    } else {
      rows.push(...(body?.results ?? []));
      url = body?.next ?? null;
    }
  }
  return rows;
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isCustomer = user?.role === 'customer';
  const isBrokerRole = user?.role === 'broker_admin' || user?.role === 'broker_employee';
  const isSuperAdmin = user?.role === 'super_admin';
  const isCountryAdmin = user?.role === 'country_admin';
  const canViewRenewalsWidget =
    !!user?.role && RENEWALS_WIDGET_ROLES.has(user.role) && !HIDE_RENEWALS_WIDGET_ROLES.has(user.role);

  // Broker 1: the "reported payment" banner is dismissible. Keyed per-user in
  // localStorage so one login's dismissal does not silently hide the banner
  // for a different login on a shared machine, and re-derived whenever the
  // logged-in user changes (not just on first mount).
  const bannerDismissKey = user?.id ? `payment-banner-dismissed:${user.id}` : null;
  const [bannerDismissed, setBannerDismissed] = useState(false);
  useEffect(() => {
    setBannerDismissed(bannerDismissKey ? localStorage.getItem(bannerDismissKey) === '1' : false);
  }, [bannerDismissKey]);
  const dismissBanner = (event: React.MouseEvent) => {
    // The banner is a <Link> wrapping its whole content -- without both of
    // these the click falls through and navigates to /quotes instead of
    // dismissing.
    event.preventDefault();
    event.stopPropagation();
    setBannerDismissed(true);
    if (bannerDismissKey) localStorage.setItem(bannerDismissKey, '1');
  };

  // CUSTOMER-2: "Assigned Advisor" rendered `user.full_name` -- the customer's
  // own name -- because the dashboard never fetched an advisor at all. Shares
  // the 'customer-me' query key with My Contacts, so the two screens agree and
  // react-query serves this from cache when both are visited.
  //
  // F6: `broker`/`broker_name` are read off the same payload -- they are one of
  // the three inputs resolveCustomerMicrosite() scores (the first-time-buyer
  // floor), so no extra request is needed for them.
  const customerMeQuery = useQuery<{
    posp_name?: string;
    posp_phone?: string;
    posp_email?: string;
    broker?: string | null;
    broker_name?: string | null;
  }>({
    queryKey: ['customer-me'],
    queryFn: async () => (await api.get('/customers/me/')).data,
    enabled: isCustomer,
  });

  // F6: the insurance types a customer can buy, from the configured set
  // (`InsuranceType`, active only, in the admin's display order) rather than a
  // hardcoded six -- so a renamed or newly added type is honoured here instead
  // of silently missing. There is no per-broker type set in the API; this is
  // the configured list a customer session can actually read, and
  // BUILT_IN_INSURANCE_TYPES is the floor when it is empty or 403s.
  //
  // Same key as useInsuranceTypesLookup(), so this shares that cache; it is
  // inlined only because the hook cannot be told to stay idle for the
  // broker/admin roles that never render tiles.
  const insuranceTypesQuery = useQuery<InsuranceTypeLookup[]>({
    queryKey: ['insurance-types', 'lookup'],
    queryFn: async () => {
      const body = (await api.get('/products/types/lookup/')).data;
      return Array.isArray(body) ? body : (body?.results ?? []);
    },
    enabled: isCustomer,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const insuranceTiles = useMemo<InsuranceTile[]>(() => {
    const configured = (insuranceTypesQuery.data ?? []).filter((row) => row?.code);
    if (configured.length === 0) return BUILT_IN_INSURANCE_TYPES.map(toTile);
    return configured.map((row) =>
      toTile({ name: row.name || titleCase(row.code), type: row.code.toLowerCase() })
    );
  }, [insuranceTypesQuery.data]);

  // Every broker this login actually holds a policy with. PolicyViewSet scopes
  // a customer by `customer__user`, so this spans all their linked Customer
  // rows -- and, being that same customer-scoped queryset, it can only ever
  // return this customer's own brokers.
  const policyBrokersQuery = useQuery<PolicyBrokerRef[]>({
    queryKey: ['customer-policy-brokers'],
    queryFn: async () => {
      const res = await api.get('/policies/?page_size=100');
      return res.data?.results ?? [];
    },
    enabled: isCustomer,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const publicBrokersQuery = useQuery<PublicBrokerRow[]>({
    queryKey: ['public-broker-directory'],
    queryFn: fetchPublicBrokers,
    enabled: isCustomer,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  // A failed input is not fatal: it just means less evidence, and at worst no
  // microsite -- which is the in-app catalog, not a dead tile.
  const isResolvingMicrosite =
    customerMeQuery.isLoading || policyBrokersQuery.isLoading || publicBrokersQuery.isLoading;

  const micrositeTarget = useMemo(
    () =>
      isResolvingMicrosite
        ? null
        : resolveCustomerMicrosite({
            policies: policyBrokersQuery.data ?? [],
            profileBrokerId: customerMeQuery.data?.broker ?? null,
            profileBrokerName: customerMeQuery.data?.broker_name ?? null,
            publicBrokers: publicBrokersQuery.data ?? [],
          }),
    [isResolvingMicrosite, policyBrokersQuery.data, customerMeQuery.data, publicBrokersQuery.data]
  );

  // Nothing is painted until BOTH the type list and the destination are known.
  // A tile rendered early would first point at /buy/:type and then silently
  // become an external new-tab link, changing where a click lands mid-hover.
  const tilesLoading = isResolvingMicrosite || insuranceTypesQuery.isLoading;

  // FIX 1A: super_admin sees a platform-scoped dashboard with only two counts
  // (Country Admins, Countries) sourced from /users/platform-stats/. The
  // policy/quote/renewal/commission data below is neither shown nor fetched.
  const { data: platformStats, isLoading: platformStatsLoading } = useQuery({
    queryKey: ['platformStats'],
    queryFn: getPlatformStats,
    enabled: isSuperAdmin,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: dashboardService.getStats,
    enabled: !isSuperAdmin,
  });

  // Queue #216/#277: the date the "Policies Added Today" tile counted and links
  // against. The API names the day it actually counted, resolved in the
  // caller's own timezone; the fallback is the BROWSER-local calendar day --
  // `toISOString()` is UTC and would name yesterday for an IST user any time
  // after 05:30 local, deep-linking to the wrong day's list.
  const localToday = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();
  const today = stats?.policies_added_today_date || localToday;

  const { data: recentPolicies, isLoading: policiesLoading } = useQuery({
    queryKey: ['recentPolicies'],
    queryFn: () => dashboardService.getRecentPolicies(5),
    // Item 22: Country Admin no longer renders this card at all (see the Main
    // Content Grid below), so the fetch is skipped rather than firing for a
    // result nothing on the page displays.
    enabled: !isSuperAdmin && !isCountryAdmin,
  });

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Welcome back! Here's an overview of your insurance platform."
      />

      {/* Customers who paid and are waiting on us to issue. A work queue, so
          it sits above everything else and links straight to the list. */}
      {isBrokerRole && !bannerDismissed && (stats?.awaiting_policy_count ?? 0) > 0 && (
        <Link
          to="/quotes"
          className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-5 py-4 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
        >
          <div>
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {stats?.awaiting_policy_count}{' '}
              {stats?.awaiting_policy_count === 1 ? 'customer has' : 'customers have'} reported
              payment
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Confirm the payment, then issue the policy and upload the document.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-medium text-amber-900 dark:text-amber-200">
              View quotes →
            </span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismissBanner}
              className="rounded-full p-1 text-amber-700 hover:bg-amber-200/60 hover:text-amber-900 dark:text-amber-300 dark:hover:bg-amber-800/40 dark:hover:text-amber-100 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </Link>
      )}

      {/* Buy a new policy - Customer Only.

          F6: the types used to be hidden behind a "Buy New Policy" button and
          each one expanded in place to preview the in-app catalog. Buying
          happens on the BROKER's public microsite, so the types are shown
          directly and every tile is a link that opens that site in a new tab --
          the dashboard stays open behind it. */}
      {isCustomer && (
        <Card className="mb-8">
          <CardHeader
            title="Buy a New Policy"
            subtitle={
              micrositeTarget
                ? `Browse and enquire on ${micrositeTarget.brokerName}'s website`
                : 'Browse the plans available through your broker'
            }
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {tilesLoading
              ? BUILT_IN_INSURANCE_TYPES.map((option) => (
                  <div
                    key={option.type}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 h-[7.5rem] animate-pulse bg-gray-50 dark:bg-gray-700/40"
                  />
                ))
              : insuranceTiles.map((option) => (
                  <InsuranceTypeTile
                    key={option.type}
                    option={option}
                    micrositeUrl={
                      micrositeTarget ? micrositeTypeUrl(micrositeTarget.slug, option.name) : null
                    }
                  />
                ))}
          </div>
        </Card>
      )}

      {/* C2 (batch 2026-09-18): "Your Advisor" (posp name/phone/email) plus its
          WhatsApp-chat and Email-support links used to render here for every
          customer login. Removed per the batch spec. `customerMe` itself is
          NOT removed -- CUSTOMER-2 above still fetches it, and F6 still reads
          `customerMe?.broker`/`broker_name` for the microsite resolution below,
          so that query stays even though nothing here renders its posp_* fields
          any more. */}

      {/* Queue #124: the Change Request card lived here. It was a menu, not a
          summary, so it moved to the sidebar (Sidebar.tsx, "Change Request").
          The dashboard front page is for what needs attention, not for
          navigation duplicated from the nav. */}

      {/* FIX 1A: Super Admin platform scope — only Country Admins + Countries */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <StatCard
            title="Country Admins"
            to={COUNTRY_ADMINS_TARGET}
            value={platformStats?.total_country_admins ?? 0}
            icon={UsersIcon}
            loading={platformStatsLoading}
          />
          <StatCard
            title="Countries"
            to={COUNTRIES_TARGET}
            value={platformStats?.active_countries ?? 0}
            icon={GlobeAltIcon}
            loading={platformStatsLoading}
          />
        </div>
      )}

      {!isSuperAdmin && (
        <>
          {/* Stats Grid — CUSTOMER-1: customers see only Total + Active Policies,
              so the grid is 2-up for them (no lg:grid-cols-4). */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${isCustomer ? '' : 'lg:grid-cols-4'} gap-4 mb-8`}>
            {/* FIX 13 (full): real month-over-month deltas come from
                /reports/dashboard-stats (MetricSnapshot history). A null delta_pct
                (no baseline yet) makes the tile render an em-dash instead of a
                fabricated growth %. */}
            <StatCard
              title="Total Policies"
              to={TOTAL_POLICIES_TARGET}
              value={stats?.total_policies ?? 0}
              icon={DocumentTextIcon}
              trend={toTrend(stats?.metrics?.total_policies?.delta_pct)}
              loading={statsLoading}
            />
            <StatCard
              title="Active Policies"
              to={ACTIVE_POLICIES_TARGET}
              value={stats?.active_policies ?? 0}
              icon={DocumentTextIcon}
              trend={toTrend(stats?.metrics?.active_policies?.delta_pct)}
              loading={statsLoading}
            />
            {/* CUSTOMER-1: Pending Quotes + Commission are broker/agent tiles —
                hidden from customer logins. */}
            {!isCustomer && (
              <StatCard
                title="Pending Quotes"
                to={pendingQuotesTarget(user?.role) ?? undefined}
                value={stats?.pending_quotes ?? 0}
                icon={ClipboardDocumentListIcon}
                trend={toTrend(stats?.metrics?.pending_quotes?.delta_pct)}
                loading={statsLoading}
              />
            )}
            {!isCustomer && <CommissionStatCard loading={statsLoading} />}
          </div>

          {/* Queue #216: what was booked today, and a way straight to it.
              Counted on created_at (when the record was ENTERED), and the link
              filters the Policies list on the same column -- the page's own
              "Today" preset dates by ISSUE date, which is a different question
              and would open a list that disagreed with this number.

              Hidden from customers: their own dashboard has no notion of a
              day's bookings, and the destination list is a broker view. */}
          {!isCustomer && (
            <div className="mb-8">
              <button
                type="button"
                onClick={() => navigate(`/policies?created_from=${today}&created_to=${today}`)}
                className="w-full rounded-xl border border-gray-200 bg-white p-5 text-left transition-colors hover:border-primary-300 hover:bg-primary-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-600 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Policies Added Today
                    </p>
                    {/* Name the day the number belongs to: "today" is
                        ambiguous across timezones, and this tile's count and
                        its link are both anchored to this exact date. */}
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="added-today-date">
                      {formatDate(today)}
                    </p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {statsLoading ? '—' : (stats?.policies_added_today ?? 0)}
                    </p>
                    <p className="mt-1 text-xs text-primary-600 dark:text-primary-400">
                      View today&apos;s policies →
                    </p>
                  </div>
                  <div className="rounded-xl bg-primary-50 p-3 dark:bg-primary-900/30">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Main Content Grid -- item 22: for Country Admin BOTH cards below
              are hidden (their real work surfaces are /policies and
              /renewals, not a dashboard summary), so the grid itself is
              skipped rather than rendering an empty two-column shell. Every
              other role is unaffected and keeps both. */}
          {(!isCountryAdmin || canViewRenewalsWidget) && (
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
              {/* #636: side by side only from 2xl; at 1440px each card was too narrow for its table. */}
              {/* Recent Policies -- item 22: hidden for Country Admin only. */}
              {!isCountryAdmin && (
                <Card>
                  <CardHeader
                    title="Recent Policies"
                    subtitle="Latest policy activities"
                    action={
                      <Link to="/policies" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                        View all
                      </Link>
                    }
                  />
                  <RecentPoliciesTable policies={recentPolicies ?? []} loading={policiesLoading} />
                </Card>
              )}

              {/* Upcoming Renewals -- W0-D: never rendered (and its query never
                  fired) for a role the renewals API refuses; see
                  RENEWALS_WIDGET_ROLES above. Item 22 additionally excludes
                  Country Admin via HIDE_RENEWALS_WIDGET_ROLES, folded into
                  canViewRenewalsWidget, even though the API itself still
                  admits that role. */}
              {canViewRenewalsWidget && (
                <Card>
                  <CardHeader
                    title="Renewals"
                    subtitle="Policies due for renewal"
                    action={
                      <Link to="/renewals" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                        View all
                      </Link>
                    }
                  />
                  <RenewalsWidget />
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {isBrokerRole && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => navigate('/tickets')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-full shadow-lg transition-colors"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Need Help?</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * F6/C1: one insurance-type tile. Always a link -- never a dead end.
 *
 * * With a microsite: the broker's storefront's TYPE page
 *   (`insurance/<type_slug>/`, via `micrositeTypeUrl` in micrositeTarget.ts),
 *   in a new tab -- not a `company/<insurer>/<policy>/` deep link, for the
 *   reason documented in BuyPolicyPage: THAT slug is not derivable from here
 *   at all, and a wrong guess is a hard 404. The type page is a narrower case
 *   -- its slug is just `slugify(type name)` -- and 404s only when the
 *   RESOLVED broker has no sellable product for this platform-wide type (see
 *   `micrositeTypeUrl`'s own doc comment for why that residual risk is
 *   accepted rather than solved here). `rel="noopener noreferrer"` because
 *   `target="_blank"` otherwise hands the opened site a live `window.opener`
 *   handle on this authenticated dashboard.
 * * Without one (broker has no public profile, or the directory call failed):
 *   the in-app catalog at /buy/:type, same tab. That page performs the same
 *   resolution itself, so if a microsite does turn out to be reachable the
 *   customer is handed over there rather than stranded.
 */
function InsuranceTypeTile({
  option,
  micrositeUrl,
}: {
  option: InsuranceTile;
  micrositeUrl: string | null;
}) {
  const className =
    'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ' +
    'hover:border-primary-400 dark:hover:border-primary-600 hover:shadow-md transition-all ' +
    'flex flex-col items-center p-4';

  const body = (
    <>
      <div className={`p-3 rounded-lg ${option.color}`}>
        <option.icon className="w-6 h-6" />
      </div>
      <span className="text-sm font-medium text-gray-900 dark:text-white mt-3 text-center">
        {option.name}
      </span>
    </>
  );

  if (micrositeUrl) {
    return (
      <a href={micrositeUrl} target="_blank" rel="noopener noreferrer" className={className}>
        {body}
        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 mt-1 dark:text-gray-500" />
      </a>
    );
  }

  return (
    <Link to={`/buy/${option.type}`} className={className}>
      {body}
    </Link>
  );
}

// FIX 13 (full): map a backend delta_pct (real month-over-month growth, or null
// when there is no historical baseline) into the StatCard trend shape. Returns
// undefined for null so the tile renders an em-dash rather than a fabricated %.
function toTrend(deltaPct: number | null | undefined): { value: number; isPositive: boolean } | undefined {
  if (deltaPct === null || deltaPct === undefined) return undefined;
  return { value: Math.abs(deltaPct), isPositive: deltaPct >= 0 };
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  loading?: boolean;
  isMonetary?: boolean;
  /** #408: when set, the whole card is a link to the matching list. */
  to?: string;
}

function StatCard({ title, value, icon: Icon, trend, loading, to }: StatCardProps) {
  if (loading) {
    return (
      <Card>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
          <div className="h-8 bg-gray-200 rounded w-16" />
        </div>
      </Card>
    );
  }

  const card = (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 dark:text-white">{value}</p>
          {trend ? (
            <div className="flex items-center mt-2">
              {trend.isPositive ? (
                <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span
                className={`text-sm font-medium ${
                  trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}
              >
                {trend.value}%
              </span>
              <span className="text-sm text-gray-500 ml-1 dark:text-gray-400">vs last month</span>
            </div>
          ) : null /* no baseline yet: show nothing, not a placeholder dash or invented delta */}
        </div>
        <div className="p-3 bg-primary-50 rounded-xl">
          <Icon className="w-6 h-6 text-primary-600" />
        </div>
      </div>
    </Card>
  );

  if (!to) return card;
  return (
    <Link
      to={to}
      aria-label={`${title}: ${value}. Open list`}
      className="block rounded-xl transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
    >
      {card}
    </Link>
  );
}

function CommissionStatCard({ loading }: { loading: boolean }) {
  // BROKER-1: rolling-window presets over each calculation's policy date. Default
  // 30d. 'custom' reveals two date inputs that send date_from/date_to, which the
  // backend treats as an explicit window overriding ?period.
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | 'custom'>('30d');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productType, setProductType] = useState('all');

  const isCustom = period === 'custom';

  const { data } = useQuery({
    queryKey: ['commissionSummary', period, productType, dateFrom, dateTo],
    queryFn: () => {
      const params = new URLSearchParams({ product_type: productType });
      if (isCustom) {
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);
      } else {
        params.set('period', period);
      }
      return api.get(`/commissions/calculations/summary/?${params.toString()}`).then((res) => res.data);
    },
  });

  if (loading) {
    return (
      <Card>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
          <div className="h-8 bg-gray-200 rounded w-16" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p
            className="text-sm text-gray-500 font-medium dark:text-gray-400"
            title={COMMISSION_CARD_TOOLTIP}
            data-testid="commission-card-label"
          >
            {COMMISSION_CARD_LABEL}
            <QuestionMarkCircleIcon className="inline-block w-4 h-4 ml-1 align-text-bottom" aria-hidden="true" />
            <span className="sr-only">{COMMISSION_CARD_TOOLTIP}</span>
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1 dark:text-white">
            {formatCurrency(data?.total_commission || '0')}
          </p>
          <div className="flex flex-col gap-2 mt-2">
            <div className="grid grid-cols-2 gap-2" data-testid="commission-card-filters">
              <div className="min-w-0">
                <SearchableSelect
                  value={period}
                  onChange={(value) => setPeriod(value as '7d' | '30d' | '90d' | 'custom')}
                  options={[
                    { value: '7d', label: '7D' },
                    { value: '30d', label: '30D' },
                    { value: '90d', label: '90D' },
                    { value: 'custom', label: 'Custom' },
                  ]}
                />
              </div>
              <div className="min-w-0">
                <SearchableSelect
                  value={productType}
                  onChange={setProductType}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'health', label: 'Health' },
                    { value: 'motor', label: 'Motor' },
                    { value: 'misc', label: 'Misc' },
                  ]}
                />
              </div>
            </div>
            {isCustom && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 p-3 bg-primary-50 rounded-xl">
          <CurrencyRupeeIcon className="w-6 h-6 text-primary-600" />
        </div>
      </div>
    </Card>
  );
}

/**
 * B-01 (Broker Admin Dashboard) / B-02 (Customer Dashboard) -- one function,
 * because there is one dashboard: App.tsx routes `/dashboard` to this page for
 * every role, so "the customer dashboard" and "the broker admin dashboard" are
 * the same component with inline role conditionals.
 *
 * Was a single days-ahead Select pinned to `bucket=upcoming` with no counts.
 * Now it mirrors the Renewals list page (`pages/renewals/RenewalsListPage.tsx`):
 * Upcoming/Lapsed tabs (the shared RenewalTabList; no counts since Cust-4/U-03),
 * each tab with its OWN
 * Expiry Window vocabulary, plus a tab-scoped Month/Year pair. Every rule it
 * needs is imported from the list page's own modules -- including the one that
 * clears a Month/Year the new tab cannot contain -- so the two surfaces cannot
 * drift into two different ideas of "upcoming".
 *
 * Every role that reaches this widget reads a TENANT-SCOPED queue: a broker
 * login sees its own brokerage, a customer sees its own book (`hide_workflow`,
 * and `apply_filters` gives a customer no filters at all). The one role whose
 * queue is unrestricted -- super_admin, whose queue is a cross-broker
 * platform book -- is kept out of the widget entirely by
 * HIDE_RENEWALS_WIDGET_ROLES above, so no row rendered here is ever wider
 * than the viewer's own book. That is load-bearing: there is no "whose book is
 * this" label anywhere below, because there does not need to be one.
 */
function RenewalsWidget() {
  // The Lapsed option list is ROLE-scoped: "All Lapsed" is the one option
  // whose whole effect is `archived=all`, and `build_queue` discards that
  // param for a customer login by design. See `lapsedWindowOptionsFor`.
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<RenewalTab>(EXPIRY_WINDOW_UPCOMING);
  // Each tab keeps its own Expiry Window, so flipping tabs and back never
  // loses the other one's selection -- and a lapsed value can never be sent
  // while `bucket=upcoming` is in effect (`windowToQueueParams`).
  // The Upcoming default stays DEFAULT_UPCOMING_RENEWALS_DAYS, so the widget
  // opens on exactly the window it opened on before this change.
  const [upcomingWindow, setUpcomingWindow] = useState(String(DEFAULT_UPCOMING_RENEWALS_DAYS));
  const [lapsedWindow, setLapsedWindow] = useState('');
  // '' means "every month" / "every year" on either tab.
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const isLapsed = activeTab === EXPIRY_WINDOW_LAPSED;
  const activeWindow = isLapsed ? lapsedWindow : upcomingWindow;
  const setActiveWindow = isLapsed ? setLapsedWindow : setUpcomingWindow;
  const tabNoun = isLapsed ? 'lapsed' : 'upcoming';

  // Same rule as the list page: switching tabs changes which Month/Year values
  // are even offered, so carrying a selection across would silently narrow the
  // new tab by a value its own Select no longer shows.
  const handleTabChange = (tab: RenewalTab) => {
    setActiveTab(tab);
    setMonth('');
    setYear('');
  };

  const yearOptions = useMemo(() => yearOptionsFor(activeTab), [activeTab]);
  const monthOptions = useMemo(() => monthOptionsFor(activeTab, year), [activeTab, year]);

  // A month that a change of tab or year has made invalid is dropped rather
  // than sent as a value this dropdown no longer offers.
  useEffect(() => {
    if (month && !monthOptions.some((option) => option.value === month)) {
      setMonth('');
    }
  }, [month, monthOptions]);

  const filters: RenewalQueueFilters = useMemo(
    () => ({
      ...windowToQueueParams(activeTab, activeWindow),
      month: month ? Number(month) : undefined,
      year: year ? Number(year) : undefined,
    }),
    [activeTab, activeWindow, month, year]
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['upcomingRenewals', filters],
    queryFn: () => renewalService.getRenewalQueue(1, filters),
  });

  const renewals = data?.results || [];

  function getRenewalStatusVariant(daysToExpiry: number): 'info' | 'warning' | 'error' {
    if (daysToExpiry < 0) return 'error';
    if (daysToExpiry <= 30) return 'warning';
    return 'info';
  }

  function getRenewalStatusLabel(daysToExpiry: number): string {
    if (daysToExpiry < 0) return 'Overdue';
    if (daysToExpiry <= 30) return 'Due';
    return 'Upcoming';
  }

  return (
    <div>
      <div className="px-6 pb-3">
        {/* Real tabs (`role="tab"`, mutually exclusive, `aria-selected`), the
            same component the Renewals list uses. Cust-4/U-03: no counts, and
            the selected tab is a solid fill, not a one-shade tint. */}
        <RenewalTabList activeTab={activeTab} onChange={handleTabChange} size="sm" />

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Keyed on the tab so a value from the other tab's vocabulary can
              never linger as a selection this options list does not have. */}
          <Select
            key={activeTab}
            label="Expiry Window"
            name="dashboard-renewals-window"
            options={isLapsed ? lapsedWindowOptionsFor(user?.role) : upcomingWindowOptions}
            value={activeWindow}
            onChange={(e) => setActiveWindow(e.target.value)}
          />
          <Select
            label="Month"
            name="dashboard-renewals-month"
            options={[{ value: '', label: 'All Months' }, ...monthOptions]}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
          <Select
            label="Year"
            name="dashboard-renewals-year"
            options={[{ value: '', label: 'All Years' }, ...yearOptions]}
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : isError ? (
        // NOT the empty state: a failed request tells us nothing about
        // whether renewals exist, so it must not render "No upcoming
        // renewals" -- that would assert something false about the book.
        <ErrorTableMessage tabNoun={tabNoun} onRetry={() => refetch()} />
      ) : renewals.length === 0 ? (
        <EmptyTableMessage message={`No ${tabNoun} renewals`} />
      ) : (
        <div className="overflow-x-auto -mx-6">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Policy</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Product</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Expiry Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Days Left</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
              {renewals.map((renewal: RenewalQueueRow) => (
                <tr key={renewal.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-3 text-sm text-gray-900 dark:text-white">{renewal.customer_name}</td>
                  <td className="px-6 py-3">
                    {/* B5/B9: the insurer's number is what a broker quotes; the
                        internal POL-... slug is a system id. `display_policy_number`
                        is computed server-side with that same preference. Same
                        preference as RecentPoliciesTable below, so the two tables
                        on this screen stop disagreeing about what "Policy #" means. */}
                    <PolicyNumber
                      source={renewal}
                      render={(text) => (
                        <Link to={`/policies/${renewal.policy_id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                          {text}
                        </Link>
                      )}
                    />
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900 dark:text-white">{renewal.product_name}</td>
                  <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDate(renewal.expiry_date)}</td>
                  <td className="px-6 py-3 text-sm text-gray-900 dark:text-white">{renewal.days_to_expiry}</td>
                  <td className="px-6 py-3">
                    <Badge variant={getRenewalStatusVariant(renewal.days_to_expiry)} size="sm">
                      {getRenewalStatusLabel(renewal.days_to_expiry)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function RecentPoliciesTable({ policies, loading }: { policies: Policy[]; loading: boolean }) {
  if (loading) {
    return <TableSkeleton rows={5} cols={4} />;
  }

  if (policies.length === 0) {
    return <EmptyTableMessage message="No recent policies" />;
  }

  return (
    <div className="overflow-x-auto -mx-6">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-700">
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Policy</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Holder</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Premium</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
          {policies.map((policy) => (
            <tr key={policy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <td className="px-6 py-3">
                <PolicyNumber
                  source={policy}
                  render={(text) => (
                    <Link to={`/policies/${policy.id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                      {text}
                    </Link>
                  )}
                />
              </td>
              <td className="px-6 py-3 text-sm text-gray-900 dark:text-white">{policy.holder_name}</td>
              <td className="px-6 py-3">
                <Badge variant={getStatusBadgeVariant(policy.lifecycle_status ?? policy.status)} size="sm">
                  {policy.lifecycle_status_display || titleCase(policy.status)}
                </Badge>
              </td>
              <td className="px-6 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(policy.gross_premium)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableSkeleton({ rows, cols }: { rows: number; cols: number }) {
  return (
    <div className="animate-pulse -mx-6">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-gray-50">
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="flex-1 h-4 bg-gray-200 rounded mr-4" />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyTableMessage({ message }: { message: string }) {
  return (
    <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
      {message}
    </div>
  );
}

function ErrorTableMessage({ tabNoun, onRetry }: { tabNoun: string; onRetry: () => void }) {
  return (
    <div className="py-8 text-center text-sm">
      <p className="text-red-600 dark:text-red-400">Couldn&apos;t load {tabNoun} renewals.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 font-medium text-primary-600 hover:text-primary-700"
      >
        Try again
      </button>
    </div>
  );
}
